// How many of a list's items fit beside another column: every item laid out, then counted
// while it ends above the column's last panel, leaving room for the button that shows the
// rest. Measured before paint, so the cut never flickers.
import { type RefObject, useLayoutEffect, useState } from "react";

/** The room the "Show N more" button takes under the last item kept. */
const BUTTON_PX = 36;

/** ``fit`` is how many of the first ``total`` children of ``list`` to show, or null for
 * all: everything fits, or the list is not beside the column (a narrow screen stacks
 * them). ``measuring`` is true while every item must be laid out to be measured. ``key``
 * changes whenever what is listed does. */
export const useFitBeside = (
  column: RefObject<HTMLElement | null>,
  list: RefObject<HTMLElement | null>,
  total: number,
  key: string,
): { fit: number | null; measuring: boolean } => {
  const [fit, setFit] = useState<number | null>(null);
  const [measuring, setMeasuring] = useState(true);

  useLayoutEffect(() => setMeasuring(true), [key, total]);

  useLayoutEffect(() => {
    if (!measuring) return;
    const last = column.current?.lastElementChild;
    const items = list.current ? [...list.current.children].slice(0, total) : [];
    if (!last || !list.current || items.length === 0) {
      setFit(null);
    } else {
      const limit = last.getBoundingClientRect().bottom;
      const bottoms = items.map((el) => el.getBoundingClientRect().bottom);
      const beside = list.current.getBoundingClientRect().top < limit;
      setFit(
        !beside || bottoms[bottoms.length - 1] <= limit
          ? null
          : Math.max(1, bottoms.filter((y) => y + BUTTON_PX <= limit).length),
      );
    }
    setMeasuring(false);
  }, [measuring, column, list, total]);

  // The column's own panels, not the column: the grid stretches the column to the row,
  // which the list itself sets, so watching it would measure in a loop.
  useLayoutEffect(() => {
    const panels = column.current ? [...column.current.children] : [];
    const watch = new ResizeObserver(() => setMeasuring(true));
    for (const panel of panels) watch.observe(panel);
    return () => watch.disconnect();
  }, [column]);

  return { fit, measuring };
};
